import React, { useState, useEffect } from "react";
import { projectType } from "../../config/action-config";
import {
  Box,
  Typography,
  TextField,
  Grid,
  Button,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  FormHelperText,
  InputAdornment,
} from "@mui/material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";

import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

import {
  createProject,
  deleteProject,
  getProjects,
  updateProject,
} from "../../api/project";
import { handleAxiosError } from "../../config/action-config";

import { projectsTable as tableDef } from "../../config/grid-columns-defs";
import ControlledTable from "../../component/ControlledTable";
import DeleteAlertDialog from "../../component/DeleteAlertDialog";
import UpdateDialog from "../../component/UpdateDialog";
import CreateDialog from "../../component/CreateDialog";
import { showAlert } from "../../actions/alert";
import { getCustomerList } from "../../api/customer";
import { getEmployeeList } from "../../api/employee";
import { useForm, Controller } from "react-hook-form";
import { Input, makeStyles, Paper } from "@material-ui/core";
import BetterControlledTable from "../../component/BetterControlledTable";

const useStyles = makeStyles((theme) => ({
  helperText: {
    color: "#c9190b",
  },
}));

const emptyUpdateData = {
  project_name: "",
  project_type: "",
  project_description: "",
  budget: "",
  start_date: "",
  end_date: "",
  status: "",
  pm_id: "",
};

const emptyNewProjectData = {
  project_name: "",
  project_description: "",
  project_type: "",
  status: "PENDING",
  start_date: "",
  end_date: "",
  customer_id: "",
  budget: "",
  pm_id: "",
};

export default function Project() {
  const classes = useStyles();
  const {
    register,
    getValues,
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm();
  const user = useSelector((state) => state.user);
  const dialogAction = useSelector((state) => state.dialog);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [updateDialogOpen, setUpdateDialogeOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(-1);
  const [updateId, setUpdateId] = useState(-1);

  const [updateData, setUpdateData] = useState(emptyUpdateData);
  const [newProjectData, setNewProjectData] = useState(emptyNewProjectData);

  const [customerList, setCustomerList] = useState([]);
  const [employeeList, setEmployeeList] = useState([]);
  const [minDate, setMinDate] = useState("");
  const { isClicked } = useSelector((state) => state.tab);
  useEffect(() => {
    dispatch({ type: "NOT_CLICKED" });
  }, [isClicked]);
  useEffect(() => {
    if (!user.token) return;
    getCustomerList(user.token)
      .then((res) => {
        setCustomerList(res.data.data);
      })
      .catch((err) => {
        handleAxiosError(err, dispatch);
      });
    getEmployeeList(user.token)
      .then((res) => {
        setEmployeeList(res.data.EmployeeList);
      })
      .catch((err) => {
        handleAxiosError(err, dispatch);
      });
  }, [user.token]);

  const onNewProjectSubmit = (data) => {
    // createProject(newProjectData, user.token)
    createProject(data, user.token)
      .then((res) => {
        dispatch(showAlert(res.data.msg));
        setNewProjectData(emptyNewProjectData);
        refreshTable();
        setCreateDialogOpen(false);
      })
      .catch((err) => {
        handleAxiosError(err, dispatch);
        setCreateDialogOpen(false);
      });
  };
  const [filterOptions, setFilterOptions] = useState({
    managers: [],
    customers: [],
  });
  const [queryFilters, setQueryFilters] = useState({
    name: "",
    manager: "",
    status: "",
    customer: "",
  });

  const refreshTable = async () => {
    setProjectTableRefreshCount((prev) => prev + 1);
  };

  function resetFilters() {
    let filtersApplied = Object.keys(queryFilters).some((k) => queryFilters[k]);
    setQueryFilters({ status: "", manager: "", customer: "", name: "" });
    if (filtersApplied) {
      setProjectTableRefreshCount((prev) => prev + 1);
    }
  }

  useEffect(() => {
    if (!dialogAction.type) return;
    switch (dialogAction.type) {
      case "EDIT": {
        setUpdateDialogeOpen(true);
        const up = getUpdatableData(dialogAction.data);
        setUpdateData(up);
        break;
      }
      case "DELETE":
        setDeleteDialogOpen(true);
        break;
      case "VIEW":
        dispatch({ type: "" });
        navigate(`${dialogAction.data.id}`);
        // dispatch({
        //   type: projectType.PROJECT_TYPE,
        //   payload: dialogAction.data,
        // });
        localStorage.setItem("projectType", dialogAction.data.project_type);
        break;
      default:
        break;
    }
  }, [dialogAction]);

  useEffect(() => {
    if (deleteId === -1) return;
    deleteProject(deleteId, user.token)
      .then((res) => {
        dispatch(showAlert(res.data.msg));
        setDeleteId(-1);
        refreshTable();
        setDeleteDialogOpen(false);
      })
      .catch((err) => {
        handleAxiosError(err, dispatch);
        setDeleteId(-1);
        setDeleteDialogOpen(false);
      });
  }, [deleteId]);

  useEffect(() => {
    if (updateId === -1) return;
    updateProject(updateId, updateData, user.token)
      .then((res) => {
        dispatch(showAlert(res.data.msg));
        setUpdateId(-1);
        setUpdateData(emptyUpdateData);
        refreshTable();
        setUpdateDialogeOpen(false);
      })
      .catch((err) => {
        handleAxiosError(err, dispatch);
        setUpdateId(-1);
        setUpdateDialogeOpen(false);
      });
  }, [updateId]);

  const changeUpdateData = (e) => {
    setUpdateData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  useEffect(() => {
    if (!createDialogOpen) {
      setNewProjectData(emptyNewProjectData);
    }
  }, [createDialogOpen]);

  const [projectTableRefreshCount, setProjectTableRefreshCount] = useState(0);
  async function getProjectsTableData(limit, offset) {
    try {
      const { data } = await getProjects(
        { limit, offset, sort: "creation_date", ...queryFilters },
        user.token
      );
      let filtersApplied = Object.keys(queryFilters).some(
        (k) => queryFilters[k]
      );
      if (!filtersApplied) {
        setFilterOptions({
          managers: data.filters.emails,
          customers: data.filters.names,
        });
      }
      return {
        rows: data.data,
        total: filtersApplied ? data.data.length : data.total,
        error: false,
      };
    } catch (error) {
      handleAxiosError(error, dispatch);
      return { error: true };
    } finally {
      // setLoading(false);
    }
  }

  return (
    <Box className="pmt-container-box">
      <Box className="pmt-title-box">
        <Box className="typography-title">
          <Typography variant="h1" className="pmt-headings-h1">
            Projects Running
          </Typography>
        </Box>
        <Button
          variant="contained"
          style={{ padding: "5px 7px" }}
          onClick={() => {
            setCreateDialogOpen(true);
          }}
          className="blue-button"
        >
          Add Project
        </Button>
      </Box>
      <Paper style={{borderRadius: "0px 0px 0px 0px"}}>
        <Grid sx={{ px: 2, pb: 1 }} container spacing={1}>
          <Grid item xs={12} sm={6} md={6} lg={3} xl={3}>
            <TextField
              label="Project Name"
              variant="outlined"
              fullWidth
              value={queryFilters.name}
              onChange={(e) =>
                setQueryFilters((prev) => ({ ...prev, name: e.target.value }))
              }
            ></TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={3} xl={3}>
            <TextField
              label="Project Manager"
              value={queryFilters.manager}
              variant="outlined"
              select
              fullWidth
              onChange={(e) =>
                setQueryFilters((prev) => ({
                  ...prev,
                  manager: e.target.value,
                }))
              }
            >
              <MenuItem value="">Select</MenuItem>
              {filterOptions?.managers?.map((m, i) => (
                <MenuItem key={i} value={m.id}>
                  {m.email}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={3} xl={3}>
            <TextField
              label="Customer"
              value={queryFilters.customer}
              variant="outlined"
              select
              fullWidth
              onChange={(e) =>
                setQueryFilters((prev) => ({
                  ...prev,
                  customer: e.target.value,
                }))
              }
            >
              <MenuItem value="">Select</MenuItem>
              {filterOptions?.customers?.map((c, i) => (
                <MenuItem key={i} value={c.id}>
                  {c.company_name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={3} xl={3}>
            <TextField
              label="Status"
              variant="outlined"
              fullWidth
              select
              value={queryFilters.status}
              onChange={(e) =>
                setQueryFilters((prev) => ({ ...prev, status: e.target.value }))
              }
            >
              <MenuItem value="">Select</MenuItem>
              <MenuItem value="COMPLETED">Completed</MenuItem>
              <MenuItem value="WIP">In Progress</MenuItem>
              <MenuItem value="PENDING">Pending</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={6} sm={6} md={6}>
            <Button
              size="small"
              variant="contained"
              onClick={resetFilters}
              className="blue-button"
            >
              Reset Filter
            </Button>
            <Button
              sx={{ ml: 1 }}
              variant="contained"
              size="small"
              className="blue-button"
              onClick={() => setProjectTableRefreshCount((prev) => prev + 1)}
            >
              Apply Filter
            </Button>
          </Grid>
          {/* <Grid item xs={6} sm={6} md={6} display="flex"></Grid> */}
        </Grid>
      </Paper>
      {/* <br /> */}
      <div className="pmt-controll-table">
        <BetterControlledTable
          hasActionButtons={true}
          getTableRows={getProjectsTableData}
          columns={tableDef}
          perPage={10}
          refreshCounter={projectTableRefreshCount}
        />
      </div>
      {/* <ControlledTable
        rows={rows}
        columnDefs={tableDef}
        getRows={getRows}
        totalCount={totalCount}
        onChange={setRows}
        onPageNumberChange={setPageNumber}
        pageSize={10}
        pageNumber={pageNumber}
        pagination={true}
        getRowNodeId={({ data }) => data?.id || data?.name}
        hasActionButtons={true}
      /> */}

      <DeleteAlertDialog
        open={deleteDialogOpen}
        setOpen={setDeleteDialogOpen}
        setDeleteId={setDeleteId}
        rowId={dialogAction.data?.id}
      />
      <UpdateDialog
        open={updateDialogOpen}
        setOpen={setUpdateDialogeOpen}
        setUpdateId={setUpdateId}
        rowId={dialogAction.data?.id}
      >
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={6}>
            <TextField
              margin="dense"
              name="project_name"
              fullWidth
              value={updateData.project_name}
              onChange={changeUpdateData}
              label="Project Name"
              type="text"
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={6}>
            <TextField
              select
              name="pm_id"
              size="small"
              margin="dense"
              label="Project Manager"
              value={updateData.pm_id}
              fullWidth
              onChange={changeUpdateData}
            >
              {employeeList.map((e, i) => (
                <MenuItem key={i} value={e.id}>
                  {e.email}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={6}>
            <FormControl fullWidth sx={{ pt: 1 }}>
              <InputLabel sx={{ pt: 1 }} id="demo-simple-select-label-tt">
                Project Type
              </InputLabel>
              <Select
                labelId="demo-simple-select-label-tt"
                id="demo-simple-select-tt"
                value={updateData.project_type || ""}
                label="Project Type"
                name="project_type"
                onChange={changeUpdateData}
              >
                <MenuItem value="FIXED">Fixed</MenuItem>
                <MenuItem value="SUBSCRIPTION">Subscription</MenuItem>
                <MenuItem value="Time & Material">Time & Material</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={6}>
            <TextField
              margin="dense"
              name="budget"
              fullWidth
              value={updateData.budget}
              onChange={changeUpdateData}
              label="Budget (USD $)"
              type="number"
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={6}>
            <FormControl fullWidth sx={{ pt: 1 }}>
              <InputLabel sx={{ pt: 1 }} id="demo-simple-select-label-us">
                Project Status
              </InputLabel>
              <Select
                labelId="demo-simple-select-label-us"
                id="demo-simple-select-us"
                value={updateData.status}
                label="Project Status"
                name="status"
                onChange={changeUpdateData}
              >
                <MenuItem value="COMPLETED">Completed</MenuItem>
                <MenuItem value="PENDING">Pending</MenuItem>
                <MenuItem value="WIP">In Progress</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          {/* <Grid item xs={12} sm={6} md={6}>
            <FormControl fullWidth sx={{ pt: 1 }}>
              <InputLabel sx={{ pt: 1 }} id="demo-simple-select-label-c">
                Customer
              </InputLabel>
              <Select
                labelId="demo-simple-select-label-c"
                id="demo-simple-select-c"
             
                label="Customer"
                name="customer_id"
                value={updateData.customer_id}
                onChange={changeUpdateData}
              >
                {customerList.map((c, i) => (
                  <MenuItem key={i} value={c.id}>
                    {c.firstname}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid> */}
          <Grid item xs={12} sm={6} md={6}>
            <TextField
              margin="dense"
              name="project_description"
              fullWidth
              value={updateData.project_description}
              onChange={changeUpdateData}
              label="Project Description"
              type="text"
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={6}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Start Date"
                inputFormat="MM/dd/yyyy"
                value={updateData.start_date}
                onChange={(newValue) => {
                  changeUpdateData({
                    target: { name: "start_date", value: newValue },
                  });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    // helperText={params?.inputProps?.placeholder}
                    fullWidth
                  />
                )}
                fullWidth
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} sm={6} md={6}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                inputFormat="MM/dd/yyyy"
                label="End Date"
                value={updateData.end_date}
                onChange={(newValue) => {
                  changeUpdateData({
                    target: { name: "end_date", value: newValue },
                  });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    // helperText={params?.inputProps?.placeholder}
                    fullWidth
                  />
                )}
              />
            </LocalizationProvider>
          </Grid>
        </Grid>
      </UpdateDialog>
      <CreateDialog
        open={createDialogOpen}
        setOpen={setCreateDialogOpen}
        onSubmit={handleSubmit(onNewProjectSubmit)}
      >
        <form onSubmit={handleSubmit(onNewProjectSubmit)}>
          <Grid container columnSpacing={2} rowSpacing={1}>
            <Grid item xs={12} sm={6} md={6}>
              <TextField
                margin="dense"
                name="project_name"
                fullWidth
                // value={newProjectData.project_name}
                // onChange={changeNewProjectData}
                label="Project Name"
                type="text"
                variant="outlined"
                inputRef={register({
                  required: "Project Name is required.",
                })}
                required
                error={Boolean(errors.project_name)}
                helperText={errors.project_name?.message}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={6}>
              <FormControl
                fullWidth
                margin="dense"
                required
                error={Boolean(errors.project_type)}
              >
                <InputLabel id="demo-simple-select-label">
                  Project Type
                </InputLabel>

                <Controller
                  render={(props) => (
                    <Select value={props.value} onChange={props.onChange}>
                      <MenuItem value="FIXED">Fixed</MenuItem>
                      <MenuItem value="SUBSCRIPTION">Subscription</MenuItem>
                      <MenuItem value="Time & Material">
                        Time & Material
                      </MenuItem>
                    </Select>
                  )}
                  name="project_type"
                  control={control}
                  // defaultValue=""
                  rules={{
                    required: "Please choose Project Type",
                  }}
                />
                <FormHelperText>{errors.project_type?.message}</FormHelperText>
              </FormControl>
              {/* <FormControl fullWidth sx={{ pt: 1 }}>
              <InputLabel sx={{ pt: 1 }} id="demo-simple-select-label-tt">
                Project Type
              </InputLabel>
              <Select
                labelId="demo-simple-select-label-tt"
                id="demo-simple-select-tt"
                value={newProjectData.project_type || ""}
                label="Project Type"
                name="project_type"
                onChange={changeNewProjectData}
              >
                <MenuItem value="FIXED">Fixed</MenuItem>
                <MenuItem value="HOURLY">Hourly</MenuItem>
                <MenuItem value="SUBSCRIPTION">Subscription</MenuItem>
              </Select>
            </FormControl> */}
            </Grid>

            <Grid item xs={12} sm={6} md={6}>
              {/* <FormControl fullWidth variant="outlined">
                <Input
                  name="budget"
                  type="number"
                  label="Budget"
                  inputRef={register({
                    required: "budget is required.",
                  })}
                  error={Boolean(errors.budget)}
                  startAdornment={<InputAdornment position="start">$</InputAdornment>}
                />
              </FormControl> */}
              <TextField
                margin="dense"
                name="budget"
                fullWidth
                // value={newProjectData.budget}
                // onChange={changeNewProjectData}
                label="Budget (USD $)"
                inputProps={{
                  startAdornment: (
                    <InputAdornment position="start">$</InputAdornment>
                  ),
                }}
                type="number"
                variant="outlined"
                inputRef={register({
                  required: "Budget is required.",
                })}
                required
                error={Boolean(errors.budget)}
                helperText={errors.budget?.message}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={6}>
              <FormControl
                // labelChange
                fullWidth
                margin="dense"
                required
                error={Boolean(errors.customer_id)}
              >
                <InputLabel id="demo-simple-select-label">
                  Customer Name
                </InputLabel>

                <Controller
                  render={(props) => (
                    <Select value={props.value} onChange={props.onChange}>
                      <MenuItem value="">Select</MenuItem>
                      {customerList.map((c, i) => (
                        <MenuItem key={i} value={c.id}>
                          {c.company_name}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                  name="customer_id"
                  control={control}
                  // defaultValue=""
                  rules={{
                    required: "Please choose Customer",
                  }}
                />
                <FormHelperText>{errors.customer_id?.message}</FormHelperText>
              </FormControl>
              {/* <FormControl fullWidth sx={{ pt: 1 }}>
              <InputLabel sx={{ pt: 1 }} id="demo-simple-select-label-c">
                Customer
              </InputLabel>
              <Select
                labelId="demo-simple-select-label-c"
                id="demo-simple-select-c"
                value={newProjectData.customer_id || undefined}
                label="Customer"
                name="customer_id"
                onChange={changeNewProjectData}
              >
                {customerList.map((c, i) => (
                  <MenuItem key={i} value={c.id}>
                    {c.email}
                  </MenuItem>
                ))}
              </Select>
            </FormControl> */}
            </Grid>
            <Grid item xs={12} sm={6} md={6}>
              <FormControl
                fullWidth
                margin="dense"
                required
                error={Boolean(errors.status)}
              >
                <InputLabel id="demo-simple-select-label">
                  Project Status
                </InputLabel>

                <Controller
                  render={(props) => (
                    <Select value={props.value} onChange={props.onChange}>
                      <MenuItem value="COMPLETED">Completed</MenuItem>
                      <MenuItem value="WIP">In Progress</MenuItem>
                      <MenuItem value="PENDING">Pending</MenuItem>
                    </Select>
                  )}
                  name="status"
                  control={control}
                  // defaultValue=""
                  rules={{
                    required: "Please choose Project Status",
                  }}
                />
                <FormHelperText>{errors.status?.message}</FormHelperText>
              </FormControl>
              {/* <FormControl fullWidth sx={{ pt: 1 }}>
              <InputLabel sx={{ pt: 1 }} id="demo-simple-select-label-s">
                Project Status
              </InputLabel>
              <Select
                labelId="demo-simple-select-label-s"
                id="demo-simple-select-s"
                value={newProjectData.status}
                label="Project Status"
                name="status"
                onChange={changeNewProjectData}
              >
                <MenuItem value="PENDING">Pending</MenuItem>
                <MenuItem value="WIP">In Progress</MenuItem>
                <MenuItem value="COMPLETED">Completed</MenuItem>
              </Select>
            </FormControl> */}
            </Grid>
            <Grid item xs={12} sm={6} md={6}>
              <FormControl
                fullWidth
                margin="dense"
                required
                error={Boolean(errors.pm_id)}
              >
                <InputLabel id="demo-simple-select-label">
                  Project Manager
                </InputLabel>
                <Controller
                  render={(props) => (
                    <Select value={props.value} onChange={props.onChange}>
                      <MenuItem value="">Select</MenuItem>
                      {employeeList.map((e, i) => (
                        <MenuItem key={i} value={e.id}>
                          {e.firstname} {e.lastname}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                  name="pm_id"
                  control={control}
                  // defaultValue=""
                  rules={{
                    required: "Please choose Project Manager",
                  }}
                />
                <FormHelperText>{errors.pm_id?.message}</FormHelperText>
              </FormControl>
              {/* <FormControl fullWidth sx={{ pt: 1 }}>
              <InputLabel sx={{ pt: 1 }} id="demo-simple-select-label-pm">
                Project Manager
              </InputLabel>
              <Select
                labelId="demo-simple-select-label-pm"
                id="demo-simple-select-pm"
                value={newProjectData.pm_id || undefined}
                label="Project Manager"
                name="pm_id"
                onChange={changeNewProjectData}
              >
                {employeeList.map((e, i) => (
                  <MenuItem key={i} value={e.id}>
                    {e.email}
                  </MenuItem>
                ))}
              </Select>
            </FormControl> */}
            </Grid>
            <Grid item xs={12} sm={6} md={6}>
              <TextField
                margin="dense"
                name="project_description"
                fullWidth
                // value={newProjectData.project_description}
                // onChange={changeNewProjectData}
                label="Project Description"
                type="text"
                variant="outlined"
                inputRef={register({
                  required: "Project Description is required.",
                })}
                required
                error={Boolean(errors.project_description)}
                helperText={errors.project_description?.message}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={6}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <Controller
                  render={(props) => (
                    <DatePicker
                      inputFormat="MM/dd/yyyy"
                      value={props.value || ""}
                      label="Start Date"
                      onChange={(nv) => {
                        props.onChange(nv);
                        setMinDate(nv);
                      }}
                      // onChange={props.onChange}
                      renderInput={(params) => (
                        <TextField
                          FormHelperTextProps={{
                            className: classes.helperText,
                          }}
                          required
                          error={Boolean(errors.start_date)}
                          helperText={errors.start_date?.message}
                          {...params}
                          fullWidth
                        />
                      )}
                    />
                  )}
                  rules={{
                    required: "Start Date is required.",
                  }}
                  name="start_date"
                  control={control}
                />
              </LocalizationProvider>
              {/* <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Start Date"
                   inputFormat="MM/dd/yyyy"
                  value={newProjectData.start_date}
                  onChange={(newValue) => {
                    changeNewProjectData({
                      target: { name: "start_date", value: newValue },
                    });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      helperText={params?.inputProps?.placeholder}
                    />
                  )}
                />
              </LocalizationProvider> */}
            </Grid>
            <Grid item xs={12} sm={6} md={6}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <Controller
                  render={(props) => (
                    <DatePicker
                      inputFormat="MM/dd/yyyy"
                      value={props.value || ""}
                      label="End Date"
                      shouldDisableDate={disablePrevDates(new Date(minDate))}
                      onChange={props.onChange}
                      renderInput={(params) => (
                        <TextField
                          fullWidth
                          FormHelperTextProps={{
                            className: classes.helperText,
                          }}
                          required
                          error={Boolean(errors.end_date)}
                          helperText={errors.end_date?.message}
                          {...params}
                        />
                      )}
                    />
                  )}
                  rules={{
                    required: "End Date is required.",
                  }}
                  name="end_date"
                  control={control}
                />
              </LocalizationProvider>
              {/* <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="End Date"
                   inputFormat="MM/dd/yyyy"
                  value={newProjectData.end_date}
                  shouldDisableDate={disablePrevDates(
                    newProjectData.start_date
                  )}
                  onChange={(newValue) => {
                    changeNewProjectData({
                      target: { name: "end_date", value: newValue },
                    });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      helperText={params?.inputProps?.placeholder}
                    />
                  )}
                />
              </LocalizationProvider> */}
            </Grid>
          </Grid>
        </form>
      </CreateDialog>
    </Box>
  );
}

// project_name, project_type, desc, budget, start_date, end, status
function getUpdatableData(data) {
  return {
    project_name: data.project_name,
    project_description: data.project_description,
    project_type: data.project_type,
    budget: data.budget,
    start_date: data.start_date,
    end_date: data.end_date,
    status: data.status,
    pm_id: data.pm_id,
  };
}
function disablePrevDates(startDate) {
  const startSeconds = Date.parse(startDate);
  return (date) => {
    return Date.parse(date) < startSeconds;
  };
}
